<template>
  <NuxtLayout
    :header-height="EDITOR_LOTTERY_HEADER_HEIGHT"
    name="sticky"
    main-class="shell-main"
    :navbar-gap="false"
  >
    <template #header>
      <EditorHeader
        v-if="lotteryId"
        v-bind="{ lotteryId }"
        @click:back="navigateToHome"
        @click:info="scrollToInfoSection"
      >
        <EditorJackpotBanner v-bind="{ config, jackpot, lotteryId }" />
      </EditorHeader>
    </template>

    <div
      class="content-wrap px-100 flex flex-col gap-200 mb-200 mt-300"
      :class="{ 'pb-800': hasControlPanel }"
    >
      <EditorCutoffTime
        v-if="lotteryId"
        v-bind="{ currentDraw, loading }"
        @cutoff-time-reached="handleCutoffTimeReached"
      />

      <slot v-if="loaded" />

      <LcSkeleton height="25rem" v-else />

      <EditorOptions v-if="optionsReady && lotteryId" ref="options" v-bind="{ lotteryId }" />

      <EditorLotteryPromoCode v-if="optionsReady && lotteryId" v-bind="{ lotteryId }" />

      <section ref="infoSection" class="flex flex-col gap-100">
        <EditorInfoExpandable
          v-if="loaded"
          v-bind="{ lotteryId }"
          v-model="isRulesOpen"
          variant="rules"
        />
        <LcSkeleton height="3rem" radius="medium" v-else />

        <EditorInfoExpandable
          v-if="loaded"
          v-bind="{ lotteryId }"
          v-model="isFaqOpen"
          variant="faqs"
        />
        <LcSkeleton height="3rem" radius="medium" v-else />
      </section>
    </div>

    <div
      v-if="hasControlPanel"
      :id="EDITOR_CONTROL_PANEL_BAR_ID"
      class="control-panel-bar py-200 px-100"
    >
      <slot name="control-panel" />
    </div>

    <template #navbar>
      <EditorLotteryPriceDetails v-if="lotteryId" v-bind="{ lotteryId }" />

      <LcButton
        class="checkout-button"
        :disabled="checkoutDisabled"
        :loading="checkoutLoading"
        @click="handleCheckout"
      >
        {{ $t('lottery.navbar.label') }}
      </LcButton>
    </template>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type { ComponentExposed } from 'vue-component-type-helpers'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config } from '../@types/editor'
import type Options from './Options.vue'

import {
  EDITOR_CHECKOUT_KEY,
  EDITOR_CONTROL_PANEL_BAR_ID,
  EDITOR_LOTTERY_HEADER_HEIGHT,
} from '../constants/editor'

type Props = {
  config: Maybe<Config>
  loaded: boolean
  lotteryId: LotteryId
  navigateToHome: () => void
  optionsReady: boolean
}

type Slots = {
  'control-panel'?: () => VNode[]
  'default'?: () => VNode[]
}

const { loaded, lotteryId } = defineProps<Props>()

const slots = defineSlots<Slots>()

const { scrollToElement } = useEditorScroll()
const infoSectionRef = useTemplateRef<HTMLElement>('infoSection')
const optionsRef = useTemplateRef<ComponentExposed<typeof Options>>('options')

const {
  checkout,
  disabled: checkoutDisabled,
  loading: checkoutLoading,
} = inject(EDITOR_CHECKOUT_KEY, {
  checkout: async () => {},
  disabled: ref(true),
  loading: ref(false),
})

const { currentDraw, handleCutoffTimeReached, jackpot, loading } = useEditorDraws(lotteryId)

useHead({ htmlAttrs: { class: 'no-scroll-anchoring' } })

const isRulesOpen = ref(false)
const isFaqOpen = ref(false)

const hasControlPanel = computed(() => loaded && !!slots['control-panel'])

const handleCheckout = async () => {
  if ((await optionsRef.value?.validate()) === false) return
  await checkout()
}

const scrollToInfoSection = () => scrollToElement(infoSectionRef.value)
</script>

<style lang="scss">
.no-scroll-anchoring {
  overflow-anchor: none;
}

// unscoped: <main> belongs to the layout, which does not carry the scope attribute of this component
.content-wrap.shell-main {
  overflow-x: clip;
  width: revert;
  max-width: revert;
  margin: revert;
  padding-inline: 0;
}
</style>

<style lang="scss" scoped>
.checkout-button {
  width: 100%;
  max-width: 480px;
}

.control-panel-bar {
  position: fixed;
  right: 0;
  bottom: $main-bottom-navigation-height;
  left: 0;
  z-index: 50;
  background: linear-gradient(
    180deg,
    token('color/transparent/blue/00/600') 0%,
    token('color/lightblue/600') 122%
  );
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
}
</style>
